import { Audience } from 'models/audience/audience';
import { Audience_Translation } from 'models/audience/audience_Translation';
import { AudienceLine } from 'models/audience/audienceLine';
import { Audience_TranslationDTO } from 'models/dto/audience/audience_TranslationDTO';
import { AudienceDTO } from 'models/dto/audience/audienceDTO';
import { AudienceLineDTO } from 'models/dto/audience/audienceLineDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import Language from 'models/language';
import { mapFromLanguage, mapToLanguage } from './langMapping';
import { mapFromResourceLinks } from './linkMapping';

export function mapFromAudiences(audienceDTOs: AudienceDTO[], cache: GlobalDataCache): Audience[] {
  const audiences = audienceDTOs.map((r) => mapFromAudience(r, cache));

  return audiences.filter((r) => r !== undefined) as Audience[];
}

export function mapFromAudience(audienceDTO: AudienceDTO | undefined, cache: GlobalDataCache): Audience | undefined {
  if (!audienceDTO) return undefined;
  const output = new Audience();
  output.audienceId = audienceDTO.audienceId;
  output.auditTrailId = audienceDTO.auditTrailId;
  output.trans = mapFromAudience_Translation(audienceDTO.trans);
  output.lines = mapFromAudienceLines(audienceDTO.lines ?? []);
  output.links = mapFromResourceLinks(audienceDTO.links ?? [], cache);

  if (output.trans && output.trans.length > 0) {
    output.transIdx = 0;
    output.name = output.trans[0].name;
    output.description = output.trans[0].description;
  }

  return output;
}

export const mapFromAudience_Translation = (transDTOs: Audience_TranslationDTO[]): Audience_Translation[] => {
  const output: Audience_Translation[] = [];

  for (const transDTO of transDTOs) {
    const newObject_Translation = new Audience_Translation();

    newObject_Translation.audienceId = transDTO.audienceId;
    newObject_Translation.description = transDTO.description;
    newObject_Translation.name = transDTO.name;
    newObject_Translation.languageId = transDTO.languageId;
    newObject_Translation.lang = mapFromLanguage(transDTO.lang) ?? new Language();

    output.push(newObject_Translation);
  }

  return output;
};

export function mapToAudience(audience: Audience): AudienceDTO {
  const output = new AudienceDTO();
  output.audienceId = audience.audienceId;
  output.trans = mapToAudience_Translation(audience.trans);
  output.lines = mapToAudienceLines(audience.lines);

  if (audience.trans && audience.transIdx >= 0) {
    output.trans[audience.transIdx].name = audience.name;
    output.trans[audience.transIdx].description = audience.description;
  }

  return output;
}

export const mapToAudience_Translation = (transDTOs: Audience_Translation[]): Audience_TranslationDTO[] => {
  const output: Audience_TranslationDTO[] = [];

  for (const transDTO of transDTOs) {
    const newObject_Translation = new Audience_TranslationDTO();

    newObject_Translation.audienceId = transDTO.audienceId;
    newObject_Translation.description = transDTO.description;
    newObject_Translation.name = transDTO.name;
    newObject_Translation.languageId = transDTO.languageId;
    newObject_Translation.lang = mapToLanguage(transDTO.lang);

    output.push(newObject_Translation);
  }

  return output;
};

export function mapFromAudienceLines(audienceLineDTOs: AudienceLineDTO[]): AudienceLine[] {
  return audienceLineDTOs.map((r) => mapFromAudienceLine(r));
}

export function mapFromAudienceLine(audienceLineDTO: AudienceLineDTO): AudienceLine {
  const output = new AudienceLine();
  output.audienceId = audienceLineDTO.audienceId;
  output.audienceLineId = audienceLineDTO.audienceLineId;
  output.userId = audienceLineDTO.userId;
  output.groupId = audienceLineDTO.groupId;
  output.roleId = audienceLineDTO.roleId;

  return output;
}

export function mapToAudienceLines(audienceLines: AudienceLine[]): AudienceLineDTO[] {
  return audienceLines.filter((r) => r.hasMember()).map((r) => mapToAudienceLine(r));
}

export function mapToAudienceLine(audienceLine: AudienceLine): AudienceLineDTO {
  const output = new AudienceLineDTO();
  output.audienceId = audienceLine.audienceId;
  output.audienceLineId = audienceLine.audienceLineId;
  output.userId = audienceLine.userId;
  output.groupId = audienceLine.groupId;
  output.roleId = audienceLine.roleId;

  return output;
}
